// /app/content/bulk — bulk generation launcher.
//
// Per docs/05-api §5.2, a bulk request specifies a date range +
// platforms + count per platform. We POST and route the user to the
// resulting bulk-job status page so they can see the stub generations
// laid out across dates.
//
// v0.1.x scope:
//   - The POST creates `pending` generation stubs. The AI doesn't run
//     yet. Users click into individual stub generations and run them
//     manually with the existing single-gen flow.
//   - When a v0.2.x worker is built, this same form spawns stubs that
//     the worker picks up. No UI change required.

import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { Select } from "../../../components/Select";
import { Input } from "../../../components/Input";
import { Textarea } from "../../../components/Textarea";
import { useI18n } from "../../../lib/i18n";
import {
  ApiError,
  brands as brandsAPI,
  bulkGenerations,
  type Brand,
  type Platform,
} from "../../../lib/api";
import { PLATFORMS as PLATFORMS_TAXA, labelOf } from "../../../lib/brand-taxonomies";

function BulkNew() {
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const search = Route.useSearch();
  const prefilledBrandId = (search as { brand_id?: string }).brand_id ?? "";

  const [brandsList, setBrandsList] = useState<Brand[] | null>(null);
  const [brandId, setBrandId] = useState<string>(prefilledBrandId);

  // Default to a sensible range: next 30 days.
  const todayISO = new Date().toISOString().slice(0, 10);
  const monthLater = new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(todayISO);
  const [endDate, setEndDate] = useState(monthLater);

  // Per-platform counts. We seed with instagram=8 — about twice a week
  // over a month, the SMB sweet spot.
  const [counts, setCounts] = useState<Record<string, number>>({ instagram: 8 });
  const [includeHolidays, setIncludeHolidays] = useState(true);

  // v0.2.48 — post type. Story mode drops non-IG/FB counts from the
  // map on switch (so the backend doesn't reject the bulk job).
  const [postType, setPostType] = useState<"feed" | "story">("feed");
  const STORY_PLATFORMS = ["instagram", "facebook"];
  useEffect(() => {
    if (postType === "story") {
      setCounts((cur) => {
        const next: Record<string, number> = {};
        for (const [pf, n] of Object.entries(cur)) {
          if (STORY_PLATFORMS.includes(pf)) next[pf] = n;
        }
        // Ensure at least IG is enabled so the user can submit.
        if (Object.keys(next).length === 0) next.instagram = 8;
        return next;
      });
    }
  }, [postType]);

  // v0.5.28 — connected-platform gating (same pattern as /content/new).
  // Disable count inputs for platforms the brand hasn't connected so
  // bulk jobs don't end up scheduling posts to platforms we can't
  // publish to.
  const [connectedPlatforms, setConnectedPlatforms] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!brandId) {
      setConnectedPlatforms(new Set());
      return;
    }
    let cancelled = false;
    Promise.all([
      brandsAPI.listSocialAccounts(brandId).catch(() => ({ social_accounts: [] })),
      brandsAPI.get(brandId).catch(() => null),
    ]).then(([sa, br]) => {
      if (cancelled) return;
      const set = new Set<string>();
      for (const a of sa.social_accounts ?? []) {
        if (a.status === "active") set.add(a.platform);
      }
      if (br?.telegram_channel && br.telegram_channel.trim() !== "") {
        set.add("telegram");
      }
      setConnectedPlatforms(set);
      // Drop counts for any platform not connected — prevents an
      // accidental submit if the operator switched brands.
      setCounts((cur) => {
        const next: Record<string, number> = {};
        for (const [pf, n] of Object.entries(cur)) {
          if (set.has(pf)) next[pf] = n;
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [brandId]);

  // Topic / prompt — the operator-meaningful theme that all children
  // share. Without this, every child collapses to generic on-brand
  // fluff (all 16 posts end up looking like clones). Empty is fine —
  // backend falls back to "Auto-scheduled <platform> post for <date>".
  const [prompt, setPrompt] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load brands the same way /content/new does — workspace-scoped list.
  useEffect(() => {
    let cancelled = false;
    brandsAPI.list().then(r => {
      if (cancelled) return;
      setBrandsList(r.brands);
      if (!prefilledBrandId && r.brands.length > 0) setBrandId(r.brands[0].id);
    }).catch(() => !cancelled && setBrandsList([]));
    return () => { cancelled = true; };
  }, [prefilledBrandId]);

  const selectedPlatforms = Object.keys(counts).filter(p => counts[p] > 0);
  const totalPosts = selectedPlatforms.reduce((s, p) => s + counts[p], 0);

  function setPlatformCount(p: string, n: number) {
    setCounts(prev => {
      const next = { ...prev };
      if (n <= 0) delete next[p];
      else next[p] = Math.min(12, Math.max(1, n)); // mirror server's 1..12 cap
      return next;
    });
  }

  async function submit() {
    if (!brandId) { setErr(t("content.bulk.err.brand")); return; }
    if (selectedPlatforms.length === 0) { setErr(t("content.bulk.err.platforms")); return; }
    if (new Date(endDate) < new Date(startDate)) {
      setErr(t("content.bulk.err.dateRange"));
      return;
    }
    setSubmitting(true); setErr(null);
    try {
      const job = await bulkGenerations.create(brandId, {
        start_date: startDate,
        end_date: endDate,
        platforms: selectedPlatforms as Platform[],
        count_per_platform: counts,
        include_holidays: includeHolidays,
        prompt: prompt.trim() || undefined,
        post_type: postType, // v0.2.48
      });
      void nav({ to: "/app/bulk-jobs/$bulkJobId", params: { bulkJobId: job.id } });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1 min-w-0">
          <Link to={brandId ? "/app/content/$brandId" : "/app/content"} params={{ brandId }}
                className="text-xs text-muted hover:text-primary ring-nexus rounded inline-block">
            ← {t("content.title")}
          </Link>
          <h1 className="font-display text-3xl font-semibold">{t("content.bulk.title")}</h1>
          <p className="text-sm text-secondary mt-1">{t("content.bulk.subtitle")}</p>
        </div>
      </div>

      <Card>
        <form onSubmit={e => { e.preventDefault(); void submit(); }} className="flex flex-col gap-5" noValidate>
          {/* Brand */}
          {brandsList === null ? (
            <p className="text-secondary text-sm">{t("common.loading")}</p>
          ) : (
            <Select
              label={t("content.bulk.field.brand")}
              value={brandId}
              onChange={e => setBrandId(e.target.value)}
              options={brandsList.map(b => ({ value: b.id, label: b.trade_name }))}
            />
          )}

          {/* Topic / prompt — operator-supplied theme that all children share */}
          <Textarea
            label={t("content.bulk.field.prompt")}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            rows={3}
            placeholder={t("content.bulk.field.promptPlaceholder")}
            hint={t("content.bulk.field.promptHint")}
            maxLength={500}
          />

          {/* Date range */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label={t("content.bulk.field.startDate")} type="date" value={startDate}
                   onChange={e => setStartDate(e.target.value)} />
            <Input label={t("content.bulk.field.endDate")} type="date" value={endDate}
                   onChange={e => setEndDate(e.target.value)}
                   hint={t("content.bulk.field.rangeHint")} />
          </div>

          {/* v0.2.48 — Post type picker. Story locks platforms to IG+FB. */}
          <fieldset className="flex flex-col gap-3 border border-border-subtle rounded-lg p-4">
            <legend className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary px-1">
              {t("gen.field.postType")}
            </legend>
            <div className="flex flex-wrap gap-2">
              {(["feed", "story"] as const).map((pt) => {
                const on = postType === pt;
                return (
                  <button
                    key={pt}
                    type="button"
                    onClick={() => setPostType(pt)}
                    aria-pressed={on}
                    className={[
                      "ring-nexus rounded-[10px] px-4 py-2 text-sm border transition flex items-center gap-2",
                      on
                        ? "bg-red text-white border-transparent shadow-md shadow-red/20"
                        : "bg-elevated border-border-default text-secondary hover:text-primary hover:border-border-strong",
                    ].join(" ")}
                  >
                    <span>{pt === "feed" ? "📰" : "📱"}</span>
                    <span>{t(`gen.field.postType.${pt}`)}</span>
                  </button>
                );
              })}
            </div>
            {postType === "story" ? (
              <p className="text-xs text-muted">{t("gen.field.postType.storyHint")}</p>
            ) : null}
          </fieldset>

          {/* Per-platform counts */}
          <fieldset className="flex flex-col gap-3 border border-border-subtle rounded-lg p-4">
            <legend className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary px-1">
              {t("content.bulk.field.counts")}
            </legend>
            <p className="text-xs text-muted">{t("content.bulk.field.countsHint")}</p>
            <div className="flex flex-col gap-2">
              {PLATFORMS_TAXA.map(taxon => {
                const value = counts[taxon.value] ?? 0;
                // v0.2.48 — disable non-IG/FB count inputs in story mode.
                const storyDisabled = postType === "story" && !STORY_PLATFORMS.includes(taxon.value);
                // v0.5.28 — disable inputs for platforms the brand
                // hasn't connected. Operator gets a visible 🔒 hint.
                const notConnected = brandId !== "" && !connectedPlatforms.has(taxon.value);
                const disabled = storyDisabled || notConnected;
                const title = storyDisabled
                  ? t("gen.field.platforms.noStoryApi")
                  : notConnected
                    ? t("gen.field.platforms.notConnected")
                    : "";
                return (
                  <div
                    key={taxon.value}
                    className={`flex items-center justify-between gap-3 ${disabled ? "opacity-40" : ""}`}
                    title={title}
                  >
                    <span className="text-sm text-primary flex-1 flex items-center gap-2">
                      {labelOf(taxon, locale)}
                      {notConnected && !storyDisabled ? (
                        <span aria-hidden className="text-[11px]">🔒</span>
                      ) : null}
                    </span>
                    <Input
                      label={labelOf(taxon, locale)}
                      type="number"
                      min={0}
                      max={12}
                      value={value}
                      disabled={disabled}
                      onChange={e => setPlatformCount(taxon.value, parseInt(e.target.value, 10) || 0)}
                      className="!w-24"
                    />
                  </div>
                );
              })}
            </div>
            {/* Connection-status hint — quick path to Integrations */}
            {brandId && connectedPlatforms.size < PLATFORMS_TAXA.length ? (
              <p className="text-xs text-muted">
                {t("gen.field.platforms.connectHint")}{" "}
                <Link
                  to="/app/brands/$brandId"
                  params={{ brandId }}
                  hash="integrations"
                  className="text-info hover:text-primary ring-nexus rounded underline"
                >
                  {t("gen.field.platforms.connectCTA")}
                </Link>
              </p>
            ) : null}
            <p className="text-xs text-muted">
              {t("content.bulk.field.totalPrefix")} <span className="font-mono">{totalPosts}</span>
            </p>
          </fieldset>

          {/* Holidays toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeHolidays}
                   onChange={e => setIncludeHolidays(e.target.checked)}
                   className="ring-nexus accent-red w-4 h-4" />
            <span className="text-sm text-secondary">{t("content.bulk.field.includeHolidays")}</span>
          </label>

          {err ? <p className="text-error text-sm" role="alert">{err}</p> : null}

          <div className="flex justify-end">
            <Button variant="primary" type="submit" loading={submitting} disabled={totalPosts === 0}>
              {t("content.bulk.submit")}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/app/content/bulk")({
  component: BulkNew,
  validateSearch: (search: Record<string, unknown>) => ({
    brand_id: typeof search.brand_id === "string" ? search.brand_id : undefined,
  }),
});
